/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { useId } from 'preact/hooks';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/InputFile/InputFile.css';

export interface InputFileProps extends Omit<HTMLAttributes<HTMLInputElement>, 'type' | 'icon'> {
  accept?: string;
  onChange?: (event: Event) => void;
  label?: string;
  multiple?: boolean;
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
}

export const InputFile: FunctionComponent<InputFileProps> = ({
  accept,
  onChange,
  label = 'Upload Document',
  icon,
  className,
  multiple,
  id: providedId,
  ...props
}) => {
  
  const generatedId = useId();
  const id = providedId || generatedId;

  const handleChange = (e: Event) => {
    onChange?.(e);
  };

  return (
    <div className={classes(['dropin-input-file', className])}>
      <label htmlFor={id} className="dropin-input-file__label">
        {icon && <span className="dropin-input-file__icon">{icon}</span>}
        {label}
      </label>
      <input
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="dropin-input-file__input"
        {...props}
      />
    </div>
  );
};